import React, { useState, useEffect } from 'react';
import { FiShield, FiUsers, FiActivity, FiLock, FiPlus } from 'react-icons/fi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import CreateAdminForm from '../components/admin/CreateAdminForm';
import ChangePasswordForm from '../components/admin/ChangePasswordForm';
import adminAuthService from '../services/adminAuth';
import toast from 'react-hot-toast';

const AdminsPage = () => {
  const { admin, isSuperAdmin } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('password');
  const [adminStats, setAdminStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Set default tab based on user role
  useEffect(() => {
    if (isSuperAdmin()) {
      setActiveTab('create');
    } else {
      setActiveTab('password');
    }
  }, [isSuperAdmin]);

  // Load admin stats for super admins
  useEffect(() => {
    if (isSuperAdmin()) {
      loadAdminStats();
    }
  }, [isSuperAdmin]);

  const loadAdminStats = async () => {
    setIsLoadingStats(true);
    try {
      const stats = await adminAuthService.getAdminStats();
      setAdminStats(stats);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      toast.error('Failed to load admin statistics');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleAdminCreated = (newAdmin) => {
    // Refresh stats after creating a new admin
    if (isSuperAdmin()) {
      loadAdminStats();
    }
  };

  const handlePasswordChanged = (result) => {
    // Handle password change success (user will be redirected to login)
    console.log('Password changed successfully');
  };

  const tabs = [
    ...(isSuperAdmin() ? [
      { id: 'create', label: 'Create Admin', icon: FiPlus },
      { id: 'stats', label: 'Statistics', icon: FiActivity }
    ] : []),
    { id: 'password', label: 'Change Password', icon: FiLock }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <FiShield className="h-8 w-8 text-primary-teal mr-3" />
          <div>
            <h1 className="text-h2 text-neutral-900">Admin Management</h1>
            <p className="text-p2-r text-neutral-600 mt-1">
              {isSuperAdmin() 
                ? 'Create new admin accounts and manage your password' 
                : 'Manage your admin account password'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Role Badge */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          <span className="text-p2-r text-neutral-600">Your Role:</span>
          <span className={`ml-2 px-3 py-1 rounded-full text-p3-b ${
            admin?.role === 'SUPERADMIN' 
              ? 'bg-purple-100 text-purple-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {admin?.role}
          </span>
        </div>
        {admin?.role === 'SUPERADMIN' && (
          <span className="text-p3-r text-green-600">✓ Can create other admins</span>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-neutral-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-primary-teal text-primary-teal'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Create Admin Tab - Super Admin Only */}
        {activeTab === 'create' && isSuperAdmin() && (
          <div className="space-y-6">
            <CreateAdminForm onAdminCreated={handleAdminCreated} />
          </div>
        )}

        {/* Statistics Tab - Super Admin Only */}
        {activeTab === 'stats' && isSuperAdmin() && (
          <div className="space-y-6">
            {/* Admin Count Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                        <FiShield className="w-5 h-5 text-purple-600" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-p3-r text-neutral-500">Super Admins</p>
                      <p className="text-h4 text-neutral-900">
                        {isLoadingStats ? '...' : adminStats?.adminCounts?.SUPERADMIN || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                        <FiUsers className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-p3-r text-neutral-500">Admins</p>
                      <p className="text-h4 text-neutral-900">
                        {isLoadingStats ? '...' : adminStats?.adminCounts?.ADMIN || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-teal-100 rounded-md flex items-center justify-center">
                        <FiActivity className="w-5 h-5 text-teal-600" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-p3-r text-neutral-500">Total Admins</p>
                      <p className="text-h4 text-neutral-900">
                        {isLoadingStats ? '...' : adminStats?.adminCounts?.total || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-l1-b text-neutral-900">Recent Admin Activity</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadAdminStats}
                    disabled={isLoadingStats}
                  >
                    {isLoadingStats ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingStats ? (
                  <div className="text-center py-8">
                    <div className="text-neutral-500">Loading activity...</div>
                  </div>
                ) : adminStats?.recentActivity?.length > 0 ? (
                  <div className="space-y-4">
                    {adminStats.recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-3 py-3 border-b border-neutral-100 last:border-b-0">
                        <div className="flex-shrink-0">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            activity.action === 'ADMIN_CREATED' ? 'bg-green-500' :
                            activity.action === 'PASSWORD_CHANGED' ? 'bg-blue-500' :
                            'bg-gray-500'
                          }`}></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-p2-r text-neutral-900">
                            {activity.action === 'ADMIN_CREATED' && 'New admin account created'}
                            {activity.action === 'PASSWORD_CHANGED' && 'Admin password changed'}
                            {!['ADMIN_CREATED', 'PASSWORD_CHANGED'].includes(activity.action) && activity.action}
                          </p>
                          <p className="text-p3-r text-neutral-500 mt-1">
                            By {activity.adminUsername} ({activity.adminRole}) • {new Date(activity.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FiActivity className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                    <div className="text-neutral-500">No recent admin activity</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Change Password Tab - All Admins */}
        {activeTab === 'password' && (
          <div className="space-y-6">
            <ChangePasswordForm onPasswordChanged={handlePasswordChanged} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminsPage;